import { useEffect, useState } from "react";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    LinearProgress,
    Tooltip,
    Menu,
    MenuItem,
    TablePagination,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Chip,
} from "@mui/material";
import newRequest from "../../utils/newRequest";
import "./Contributions.scss";
import ExpandableComponent from "../expandableComponent/ExpandableComponent";
import { motion } from "framer-motion";
import { FiSearch } from "react-icons/fi";
import numeral from "numeral";
import { MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import OnScreenReports from "../onScreenReports/OnScreenReports";
import CircularButton, { CustomLoadingButton } from "../ui/CustomButton";
import { useSelector } from "react-redux";
import { IoDocumentTextSharp } from "react-icons/io5";

const Contributions = ({ entCode, showStatement = "N", entityId = null }) => {
    // console.log(`contributions mounted...entCode ${entCode} ... showStatement ${showStatement} .. entityId${entityId}`);

    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedContribution, setSelectedContribution] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [contributionSummary, setContributionSummary] = useState(null);
    const [contributions, setContributions] = useState([]);
    const [currentContributionPage, setCurrentContributionPage] = useState(0);
    const [entityData, setEntityData] = useState(null);

    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
    const [reportGroupArray, setReportKeysArray] = useState(["contribution"]);

    const handleContributionPageChange = (event, newPage) => {
        setCurrentContributionPage(newPage);
    };

    const handleContributionRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentContributionPage(0);
    };

    const filteredContributions = contributions?.filter(
        (row) =>
            row.ln_hd_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.hd_which_period?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.hd_which_year?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            row.hd_ref_no?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedContributions = filteredContributions?.slice(
        currentContributionPage * rowsPerPage,
        currentContributionPage * rowsPerPage + rowsPerPage
    );

    const fetchContributions = async () => {
        try {
            const res = await newRequest.post("/arReceiptLn/contributions", { ln_ent_code: entCode });
            // console.log(`fetchContributions ${JSON.stringify(res.data, null, 2)}`);
            setContributions(res.data.docs);
            setContributionSummary(res.data.summary);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchEntity = async (id) => {
        try {
            const res = await newRequest.get(`/allEntity/findById/${id}`);
            setEntityData(res.data);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchContributions();
        // if show statement is flagged Y and id is PasswordRounded, fetch entity data 
        if (showStatement === "Y" && entityId) {
            fetchEntity(entityId);
        }
    }, [entCode]);

    return (
        <motion.div
            className="contributions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}>
            <ExpandableComponent
                title={t('contributions').toUpperCase()}
                subtitle={
                    <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                        {showStatement === "Y" && entityId &&
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                height={"32px"}
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                {t('statement').toUpperCase()}
                            </CustomLoadingButton>}
                        {paginatedContributions > rowsPerPage &&
                            <div className="search-input-wrapper" style={{ width: "60%" }}>
                                <FiSearch className="search-input-icon" />
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder={`${t('search')} ${t('contributions')}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm?.length > 0 && (
                                    <MdClose
                                        onClick={async (e) => {
                                            try {
                                                setSearchTerm("");
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                        className="search-input-icon"
                                    />
                                )}
                                {searchTerm?.length === 0 && (
                                    <span className="search-input-icon" />
                                )}
                            </div>}
                    </div>
                }
                bgColor="#fff"
                defaultExpanded={true}
            >
                <div className="contributions-wrapper">
                    <hr className="separator-h" />
                    <h1 className="main-title">{t('summary').toUpperCase()}</h1>
                    <div className="contributions-summary">
                        <h1 className="main-title">{contributionSummary?.total_count === 1 ? `${contributionSummary?.total_count} ${t('contribution').toUpperCase()}` : `${contributionSummary?.total_count} ${t('contributions').toUpperCase()}`}</h1>
                        <h1 className="main-title">{`${t('amount').toUpperCase()} ${numeral(contributionSummary?.total_amount).format("0,0.00")}`}</h1>
                    </div>
                    <hr className="separator-h" />
                    {paginatedContributions?.length > 0 &&
                        <div className="table-content" style={{ marginTop: "20px" }} >
                            <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                <Table sx={{ width: "max-content", border: 'none' }}>
                                    <TableHead className="tablehead">
                                        <TableRow className="tablerow">
                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                #
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                <span>{t('year').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                <span>{t('period').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                <span>{t('mode').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                <span>{t('documentNo').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                <span>{t('date').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell right" style={{ maxWidth: "200px" }}>
                                                <span>{t('amount').toUpperCase()}</span>
                                            </TableCell>
                                            {/* <TableCell className="table-cell left" style={{ width: "auto", display: 'flex' }}>
                              <span>RECEIVED BY</span>
                            </TableCell> */}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody className="tablebody">
                                        {paginatedContributions?.map((row, index) => (
                                            <TableRow
                                                key={row._id}
                                                className={`${selectedContribution?._id === row._id ? "tablerow current" : "tablerow"}`}
                                                onClick={() => {
                                                    try {
                                                        setSelectedContribution(row);
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                            >
                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                    {currentContributionPage * rowsPerPage + index + 1}
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                    <span className="sub-title">{row?.hd_which_year}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "80px" }}>
                                                    <span className="sub-title">{row?.hd_which_period}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                    <span className="sub-title">{row?.hd_mode}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                    <span className="sub-title">{row?.ln_hd_no}</span>
                                                </TableCell>
                                                <TableCell className="table-cell left" style={{ maxWidth: "120px" }}>
                                                    <span className="sub-title">{row?.formatted_gldate_xx}</span>
                                                </TableCell>
                                                <TableCell className="table-cell right" style={{ maxWidth: "200px" }}>
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: "flex-start", gap: "5px" }}>
                                                        <span className="sub-title xrsmall">{row?.hd_cur_code}</span>
                                                        <span className="sub-title">{numeral(row?.ln_fc_amount).format("0,0.00")}</span>
                                                    </div>
                                                </TableCell>
                                                {/* <TableCell className="table-cell left" style={{ width: "auto" }}>
                                <span className="sub-title">{row?.updated_by_name_xx}</span>
                              </TableCell> */}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {contributions?.length > rowsPerPage
                                    && <TablePagination
                                        rowsPerPageOptions={[10]}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleContributionRowsPerPage}
                                        component="div"
                                        count={contributions?.length}
                                        rows={contributions}
                                        page={currentContributionPage}
                                        onPageChange={handleContributionPageChange}
                                    />}
                            </TableContainer>
                        </div>}
                </div>
            </ExpandableComponent>
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('reports')}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={reportGroupArray}
                                documentData={{
                                    docNo: entCode,
                                    docType: "Contribution",
                                    docId: entCode,
                                    entityData
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    )
}

export default Contributions